import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { AlertTriangle, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import HorsePickerInline from './HorsePickerInline';

const fmt = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * GenerateCareInvoiceModal
 * - single-horse mode: pass `horse` prop (pre-selected, no picker shown)
 * - multi-horse mode:  omit `horse` prop (HorsePickerInline shown, horses fetched internally)
 */
export default function GenerateCareInvoiceModal({ open, onClose, horse }) {
  const navigate = useNavigate();

  const today     = new Date().toLocaleDateString('sv');
  const firstOfMonth = today.slice(0, 8) + '01';

  const [dateFrom, setDateFrom]         = useState(firstOfMonth);
  const [dateTo, setDateTo]             = useState(today);
  const [selectedHorseIds, setSelectedHorseIds] = useState([]);
  const [availableHorses, setAvailableHorses]   = useState([]);
  const [previewEntries, setPreviewEntries]     = useState([]);
  const [step, setStep]                 = useState('form'); // form | previewing | preview | generating | done
  const [results, setResults]           = useState(null);

  const isMultiMode = !horse;

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setDateFrom(firstOfMonth);
    setDateTo(today);
    setSelectedHorseIds(horse ? [horse.id] : []);
    setPreviewEntries([]);
    setStep('form');
    setResults(null);
    if (isMultiMode) {
      api.get('/farmfolio/global/horses').then(r => setAvailableHorses(r.data || [])).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const horseIds = isMultiMode ? selectedHorseIds : (horse ? [horse.id] : []);

  const handlePreview = async () => {
    if (!horseIds.length) { toast.error('Select at least one horse'); return; }
    if (!dateFrom || !dateTo) { toast.error('Date range is required'); return; }
    setStep('previewing');
    try {
      // ── Care log billable entries ──────────────────────────────────────────
      let allEntries = [];
      if (isMultiMode) {
        const res = await api.get('/farmfolio/global/care-log');
        allEntries = (res.data || []).filter(e =>
          horseIds.includes(e.horse_id) && (e.is_billable === true || e.billable === true) &&
          (e.cost > 0 || !!e.care_catalog_item_id) &&
          e.log_date >= dateFrom && e.log_date <= dateTo &&
          !e.is_from_routine
        );
      } else {
        const res = await api.get(`/farmfolio/care-log?horse_id=${horse.id}`);
        allEntries = (res.data || []).filter(e =>
          (e.is_billable === true || e.billable === true) &&
          (e.cost > 0 || !!e.care_catalog_item_id) &&
          e.log_date >= dateFrom && e.log_date <= dateTo &&
          !e.is_from_routine
        ).map(e => ({ ...e, horse_name: horse.system_name || horse.name }));
      }
      allEntries.sort((a, b) => (a.log_date > b.log_date ? 1 : -1));

      // ── Routine billable items (boarding, feed, meds) ──────────────────────
      const routineItems = [];
      try {
        const catRes = await api.get('/farmfolio/care-catalog');
        const catalogById = {};
        (catRes.data || []).forEach(c => { catalogById[c.id] = c; });

        const df = new Date(dateFrom + 'T12:00:00');
        const dt = new Date(dateTo   + 'T12:00:00');
        const totalDays = Math.round((dt - df) / 86400000) + 1;

        // Count already-logged per catalog item for deduplication
        const loggedCounts = {};
        allEntries.forEach(e => {
          if (e.care_catalog_item_id) {
            loggedCounts[e.care_catalog_item_id] = (loggedCounts[e.care_catalog_item_id] || 0) + 1;
          }
        });

        const seenIds = new Set();
        const processItem = (item, horseName, horseId) => {
          const cid = item?.care_catalog_item_id || item?.catalog_item_id;
          if (!cid || seenIds.has(`${cid}:${horseId}`)) return;
          const cat = catalogById[cid];
          const isBillable = cat?.is_billable ?? cat?.billable;
          const billingCost = cat?.account_rate || cat?.cost || 0;
          if (!isBillable || !(billingCost > 0)) return;

          // Effective date window for this item
          const effFrom = item.effective_from ? new Date(item.effective_from + 'T12:00:00') : df;
          const effTo   = item.effective_to   ? new Date(item.effective_to   + 'T12:00:00') : dt;
          const start   = effFrom > df ? effFrom : df;
          const end     = effTo   < dt ? effTo   : dt;
          if (start > end) return;
          const days = Math.round((end - start) / 86400000) + 1;
          const qty  = days - (loggedCounts[cid] || 0);
          if (qty <= 0) return;

          seenIds.add(`${cid}:${horseId}`);
          const nameStr = cat.name || '';
          const typeStr = cat.type || '';
          const desc = nameStr.toLowerCase().startsWith(typeStr.toLowerCase()) ? nameStr : typeStr ? `${typeStr} — ${nameStr}` : nameStr;
          routineItems.push({
            id: `routine-${cid}-${horseId}`,
            horse_id: horseId, horse_name: horseName,
            is_routine: true,
            name: cat.name, type: typeStr, category: cat.category || 'Daily Care',
            cost: cat.account_rate || cat.cost || 0,
            cost_currency: cat.cost_currency || 'USD',
            units: qty, description: desc,
          });
        };

        const processRoutine = async (horseId, horseName) => {
          const rRes = await api.get(`/farmfolio/horse/${horseId}/routine`);
          const r = rRes.data || {};
          const items = [
            ...(r.feed_entries || []),
            ...(r.med_entries  || []),
            ...(r.boarding?.care_catalog_item_id ? [r.boarding] : []),
            ...(r.routine_history || []),
          ];
          items.forEach(item => processItem(item, horseName, horseId));
        };

        for (const hid of horseIds) {
          const hn = isMultiMode
            ? availableHorses?.find(h => h.id === hid)?.system_name || hid
            : (horse?.system_name || horse?.name || '');
          await processRoutine(hid, hn);
        }
      } catch { /* silently skip routine preview on error */ }

      setPreviewEntries([...allEntries, ...routineItems]);
      setStep('preview');
    } catch {
      toast.error('Failed to load care log preview');
      setStep('form');
    }
  };

  const handleGenerate = async () => {
    setStep('generating');
    try {
      const res = await api.post('/farmfolio/generate-invoice', {
        horse_ids: horseIds,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setResults(res.data);
      setStep('done');
      if (res.data.created?.length > 0) {
        toast.success(`Created ${res.data.created.length} draft invoice(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate invoices');
      setStep('preview');
    }
  };

  const previewTotal = previewEntries.reduce((s, e) => {
    const units = e.is_routine ? parseFloat(e.units) || 0 : parseFloat(e.units) || 1;
    return s + (parseFloat(e.cost) || 0) * units;
  }, 0);
  const previewCurrency = previewEntries[0]?.cost_currency || 'USD';

  // group preview by horse for display
  const previewByHorse = previewEntries.reduce((acc, e) => {
    const key = e.horse_id;
    if (!acc[key]) acc[key] = { horse_name: e.horse_name || e.horse_id, entries: [] };
    acc[key].entries.push(e);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Invoice from Care Logs</DialogTitle>
        </DialogHeader>

        {/* ── Form Step ── */}
        {(step === 'form' || step === 'previewing') && (
          <div className="space-y-4 py-2">
            {isMultiMode && (
              <div className="space-y-1">
                <HorsePickerInline
                  availableHorses={availableHorses}
                  value={selectedHorseIds}
                  onChange={setSelectedHorseIds}
                  mode="multi"
                />
              </div>
            )}
            {!isMultiMode && (
              <div className="space-y-1">
                <Label className="font-semibold">Horse</Label>
                <Input value={horse?.system_name || horse?.name || ''} disabled className="bg-muted/30 opacity-70" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="font-semibold">Date From</Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="time-filled" />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Date To</Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="time-filled" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Only <span className="font-medium">billable</span> care log entries with a cost set will be included.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handlePreview} disabled={step === 'previewing'}
                className="bg-primary hover:bg-primary/90 text-white">
                {step === 'previewing' ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Loading…</> : 'Preview Entries'}
              </Button>
            </div>
          </div>
        )}

        {/* ── Preview Step ── */}
        {step === 'preview' && (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">
                {previewEntries.length} billable {previewEntries.length === 1 ? 'item' : 'items'} found
                <span className="ml-2 text-muted-foreground font-normal">({fmtDate(dateFrom)} – {fmtDate(dateTo)})</span>
              </p>
              <span className="text-sm font-medium">{fmt(previewTotal, previewCurrency)}</span>
            </div>

            {previewEntries.length === 0 ? (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                No billable care entries found for the selected horse(s) and date range.
              </div>
            ) : (
              <div className="border rounded overflow-hidden max-h-[340px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/10 border-b">
                    <tr>
                      {isMultiMode && <th className="text-left p-2 font-medium">Horse</th>}
                      <th className="text-left p-2 font-medium">Date / Source</th>
                      <th className="text-left p-2 font-medium">Description</th>
                      <th className="text-right p-2 font-medium">Qty</th>
                      <th className="text-right p-2 font-medium">Unit Cost</th>
                      <th className="text-right p-2 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(previewByHorse).map(({ horse_name, entries }) =>
                      entries.map((e, i) => {
                        const nameStr = e.name || '';
                        const typeStr = e.type || '';
                        const desc = e.description || (nameStr.toLowerCase().startsWith(typeStr.toLowerCase())
                          ? nameStr : typeStr ? `${typeStr} — ${nameStr}` : nameStr);
                        const units = parseFloat(e.units) || 1;
                        const cost  = parseFloat(e.cost) || 0;
                        return (
                          <tr key={e.id || i} className={`border-b hover:bg-muted/20 ${e.is_routine ? 'bg-sky-50/40' : ''}`}>
                            {isMultiMode && i === 0 && (
                              <td className="p-2 font-medium" rowSpan={entries.length}>{horse_name}</td>
                            )}
                            <td className="p-2 whitespace-nowrap">
                              {e.is_routine
                                ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-sky-100 text-sky-700 font-medium">Routine ×{units}d</span>
                                : fmtDate(e.log_date)}
                            </td>
                            <td className="p-2">{desc}</td>
                            <td className="p-2 text-right">{e.is_routine ? units : units}</td>
                            <td className="p-2 text-right">{fmt(cost, e.cost_currency || 'USD')}</td>
                            <td className="p-2 text-right">{fmt(cost * units, e.cost_currency || 'USD')}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              One draft invoice will be created per owner per horse. Prices shown are base costs — expense markup (if set on the owner's account) is applied at generation time.
            </p>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setStep('form')}>Back</Button>
              <Button onClick={handleGenerate} disabled={previewEntries.length === 0}
                className="bg-primary hover:bg-primary/90 text-white">
                Generate Draft Invoices
              </Button>
            </div>
          </div>
        )}

        {/* ── Generating Step ── */}
        {step === 'generating' && (
          <div className="py-12 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm">Creating draft invoices…</p>
          </div>
        )}

        {/* ── Done Step ── */}
        {step === 'done' && results && (
          <div className="space-y-4 py-2">
            {results.created?.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-semibold text-green-700">{results.created.length} draft invoice(s) created</p>
                </div>
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/10 border-b">
                      <tr>
                        <th className="text-left p-2 font-medium">Invoice #</th>
                        <th className="text-left p-2 font-medium">Horse</th>
                        <th className="text-left p-2 font-medium">Owner</th>
                        <th className="text-right p-2 font-medium">Total</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.created.map(inv => (
                        <tr key={inv.invoice_id} className="border-b hover:bg-muted/20">
                          <td className="p-2 font-medium">{inv.invoice_number}</td>
                          <td className="p-2">{inv.horse_name}</td>
                          <td className="p-2">{inv.owner_name}</td>
                          <td className="p-2 text-right">{fmt(inv.total, inv.currency)}</td>
                          <td className="p-2">
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                              onClick={() => { onClose(); navigate(`/invoices/${inv.invoice_id}`); }}>
                              <ExternalLink className="w-3 h-3 mr-1" /> View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {results.warnings?.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-semibold text-yellow-700 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {results.warnings.length} horse(s) skipped
                </p>
                {results.warnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1.5">
                    <span className="font-medium">{w.horse_name}</span>
                    <span>—</span>
                    <span>
                      {w.reason === 'no_billable_entries'
                        ? 'No billable care entries in this date range'
                        : 'No ownership records linked — add owners on the horse\'s Ownership tab'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {results.created?.length === 0 && results.warnings?.length === 0 && (
              <p className="text-sm text-muted-foreground italic text-center py-4">No invoices were created.</p>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={onClose}>Close</Button>
              {results.created?.length === 1 && (
                <Button className="bg-primary hover:bg-primary/90 text-white"
                  onClick={() => { onClose(); navigate(`/invoices/${results.created[0].invoice_id}`); }}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open Draft Invoice
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
